import React from 'react';
import * as R from 'ramda';
import { useDispatch } from 'react-redux';
// components
import { Checkbox } from '../../../components/table/ui';
import { TextComponent } from '../../../components/text';
// features
import { setExternalIdFilter } from '../../edi-integration/actions';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// utilities
import routesMap from '../../../utilities/routes';
// ui
import { Box, Flex } from '../../../ui';
//////////////////////////////////////////////////

const colorsMap = {
  [GC.NOTIFICATION_WARNING_LEVEL_TYPE_INFO]: 'colors.bgLightBlue',
  [GC.NOTIFICATION_WARNING_LEVEL_TYPE_CRITICAL]: 'colors.light.lightPink',
  [GC.NOTIFICATION_WARNING_LEVEL_TYPE_WARNING]: 'colors.light.warningBackground',
};

export const StatusComponent = ({ warningType }) => (
  <Flex
    py='3px'
    width={70}
    fontSize={13}
    borderRadius={10}
    justifyContent='center'
    color={G.getTheme('colors.dark.mainDark')}
    bg={G.getTheme(G.getPropFromObject(warningType, colorsMap))}
  >
    {G.getEnumLocale(warningType)}
  </Flex>
);

const getItemRoute = (type, objectGuid, typeGroup) => {
  const route = R.path([type, GC.FIELD_ROUTE], typeGroup);

  if (G.isString(route)) return route;

  if (G.isFunction(route)) return route(objectGuid);

  return GC.ROUTE_PATH_HOME;
};

const bgColor = G.getTheme('colors.#F8FAFD');
const azureColor = G.getTheme('colors.azure');
const greenColor = G.getTheme('colors.light.green');
const blackColor = G.getTheme('colors.light.black');

const typeGroup = {
  [GC.NOTIFICATION_OBJECT_TYPE_TEL]: {
    locale: ['titles:tel', 'Trip'],
    route: routesMap.dispatchDetailsLoad,
    color: G.getTheme('colors.light.red'),
  },
  [GC.NOTIFICATION_OBJECT_TYPE_CLO]: {
    color: greenColor,
    locale: ['titles:clo', 'Order'],
    route: routesMap.dispatchDetailsOrder,
  },
  [GC.NOTIFICATION_OBJECT_TYPE_EDI]: {
    color: greenColor,
    route: routesMap.ediList,
    locale: ['titles:edi', 'EDI'],
  },
  [GC.NOTIFICATION_OBJECT_TYPE_DRIVER]: {
    color: blackColor,
    locale: ['titles:driver', 'Driver'],
    route: routesMap[G.getFleetProfileRoutePathNameByFleetType()],
  },
  [GC.NOTIFICATION_OBJECT_TYPE_IFTA]: {
    color: blackColor,
    route: routesMap.iftaReport,
    locale: ['titles:ifta', 'IFTA'],
  },
  [GC.NOTIFICATION_OBJECT_TYPE_TRUCK]: {
    color: blackColor,
    locale: ['titles:truck', 'Truck'],
    route: routesMap[G.getFleetProfileRoutePathNameByFleetType('truck')],
  },
  [GC.NOTIFICATION_OBJECT_TYPE_TRAILER]: {
    color: blackColor,
    locale: ['titles:trailer', 'Trailer'],
    route: routesMap[G.getFleetProfileRoutePathNameByFleetType('trailer')],
  },
  [GC.NOTIFICATION_OBJECT_TYPE_CARRIER]: {
    color: blackColor,
    route: routesMap.carrierProfile,
    locale: ['titles:carrier', 'CARRIER'],
  },
  [GC.NOTIFICATION_OBJECT_TYPE_ORDER_QUOTE]: {
    color: blackColor,
    route: routesMap.orderQuotes,
    locale: ['titles:order-quote', 'Order Quote'],
  },
};

export const GroupComponent = ({ name, type, objectGuid, actionHide }) => {
  const dispatch = useDispatch();

  const handleClick = (e) => {
    if (R.equals(type, GC.NOTIFICATION_OBJECT_TYPE_EDI)) dispatch(setExternalIdFilter(name));

    G.callFunctionWithArgs(actionHide, e);
    G.goToRoute(getItemRoute(type, objectGuid, typeGroup));
  };

  const title = `${G.getWindowLocale(...R.pathOr([], [type, 'locale'], typeGroup))}: ${name}`;

  return (
    <TextComponent
      p='4px 10px'
      title={title}
      fontSize={14}
      maxWidth={370}
      display='block'
      fontWeight={700}
      cursor='pointer'
      textAlign='center'
      withEllipsis={true}
      onClick={handleClick}
      color={R.pathOr(greenColor, [type, 'color'], typeGroup)}
    >
      {title}
    </TextComponent>
  );
};

export const NotificationItem = (props) => {
  const {
    guid,
    read,
    check,
    message,
    selected,
    displayId,
    actionHide,
    objectGuid,
    readAction,
    objectType,
    createdDate,
    withoutGroup,
    warningLevel,
    withCheckbox,
  } = props;

  return (
    <Flex
      p={10}
      pl='4px'
      gap='5px'
      bg={bgColor}
      width='100%'
      minHeight={60}
      borderRadius={10}
      borderLeft='5px solid'
      borderColor={G.ifElse(read, bgColor, azureColor)}
    >
      {
        withCheckbox &&
        <Checkbox type='checkbox' mainColors={true} checked={Boolean(selected)} onChange={() => check(guid)} />
      }
      <Flex ml={15} gap='5px' width='100%' flexDirection='column' alignItems='flex-start'>
        <Flex width='100%' justifyContent='space-between' color={G.getTheme('colors.light.darkGrey')}>
          <Flex gap='5px'>
            <StatusComponent type={objectType} warningType={warningLevel} />
            {
              R.not(withoutGroup) &&
              <GroupComponent
                name={displayId}
                type={objectType}
                actionHide={actionHide}
                objectGuid={objectGuid}
              />
            }
          </Flex>
          <TextComponent fontSize={12} whiteSpace='normal'>{createdDate}</TextComponent>
        </Flex>
        <Flex width='100%' justifyContent='space-between'>
          <TextComponent
            fontSize={15}
            maxWidth={550}
            display='block'
            title={message}
            cursor='pointer'
            color={blackColor}
            withEllipsis={true}
            onClick={() => G.callFunctionWithArgs(readAction, props)}
          >
            {message}
          </TextComponent>
          {
            G.isFalse(read) &&
            <Box
              width='7px'
              height='7px'
              bg={azureColor}
              cursor='pointer'
              borderRadius='50%'
              onClick={() => G.callFunctionWithArgs(readAction, props)}
              title={G.getWindowLocale('actions:mark-as-read', 'Mark as read')}
            />
          }
        </Flex>
      </Flex>
    </Flex>
  );
};
